import React from "react";
import { FaGripVertical, FaCalendarAlt, FaUser } from "react-icons/fa";
import {
  PRIORITY_STYLES,
  CATEGORY_STYLES,
  assigneesLabel,
  formatDate,
  getExpandedAssignees,
  isOverdue,
} from "../../utils/taskHelpers";

const TaskCard = ({ task, onOpen, onDragStart }) => {
  const priorityClass = PRIORITY_STYLES[task.Priority] || PRIORITY_STYLES.Medium;
  const overdue = isOverdue(task.DueDate, task.expand?.Status);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onClick={() => onOpen(task)}
      className="group cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-[#90c740] hover:shadow-md"
    >
      <div className="mb-2 flex items-start gap-2">
        <FaGripVertical className="mt-1 shrink-0 text-gray-300 opacity-0 transition group-hover:opacity-100" />
        <div className="min-w-0 flex-1">
          <h4 className="truncate font-semibold text-gray-900">{task.Title}</h4>
          {task.Description && (
            <p
              className="mt-1 line-clamp-2 text-xs text-gray-500"
              dangerouslySetInnerHTML={{
                __html: String(task.Description).replace(/<[^>]+>/g, " ").trim().slice(0, 120),
              }}
            />
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {task.Category && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_STYLES[task.Category] || "bg-gray-100 text-gray-700"}`}>
            {task.Category}
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${priorityClass}`}>
          {task.Priority || "Medium"}
        </span>
        {task.DueDate && (
          <span
            className={`flex items-center gap-1 text-xs ${
              overdue ? "font-medium text-red-600" : "text-gray-500"
            }`}
          >
            <FaCalendarAlt className="text-[10px]" />
            {formatDate(task.DueDate)}
          </span>
        )}
      </div>

      {getExpandedAssignees(task).length > 0 && (
        <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
          <FaUser className="text-[10px]" />
          <span className="truncate">{assigneesLabel(task)}</span>
        </div>
      )}

      {Array.isArray(task.Checklist) && task.Checklist.length > 0 && (
        <div className="mt-2 text-xs text-gray-400">
          {task.Checklist.filter((i) => i.done).length}/{task.Checklist.length} checklist items
        </div>
      )}
    </div>
  );
};

const StatusColumn = ({
  status,
  columnTasks,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpenTask,
  onDragStart,
}) => (
  <div
    className="flex min-w-0 flex-col"
    onDragOver={(e) => onDragOver(e, status.id)}
    onDragLeave={onDragLeave}
    onDrop={(e) => onDrop(e, status.id)}
  >
    <div
      className="mb-3 flex items-center justify-between rounded-lg px-3 py-2"
      style={{ backgroundColor: `${status.Color || "#94a3b8"}20` }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: status.Color || "#94a3b8" }}
        />
        <h3 className="truncate font-semibold text-gray-800">{status.Name}</h3>
      </div>
      <span className="ml-2 shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
        {columnTasks.length}
      </span>
    </div>

    <div
      className={`max-h-[360px] min-h-[200px] flex-1 space-y-3 overflow-y-auto rounded-xl border-2 border-dashed p-2 transition ${
        isDragOver ? "border-[#90c740] bg-green-50/50" : "border-transparent bg-gray-50/50"
      }`}
    >
      {columnTasks.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">Drop tasks here</p>
      ) : (
        columnTasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onOpen={onOpenTask}
            onDragStart={onDragStart}
          />
        ))
      )}
    </div>
  </div>
);

const COLUMNS_PER_ROW = 3;

const TaskKanbanBoard = ({
  statuses,
  tasksByStatus,
  onOpenTask,
  onDragStart,
  onDrop,
  dragOverStatusId,
  onDragOver,
  onDragLeave,
}) => {
  const rows = [];
  for (let i = 0; i < statuses.length; i += COLUMNS_PER_ROW) {
    rows.push(statuses.slice(i, i + COLUMNS_PER_ROW));
  }

  const renderRow = (rowStatuses) => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rowStatuses.map((status) => (
        <StatusColumn
          key={status.id}
          status={status}
          columnTasks={tasksByStatus[status.id] || []}
          isDragOver={dragOverStatusId === status.id}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onOpenTask={onOpenTask}
          onDragStart={onDragStart}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      {rows.map((rowStatuses, index) => (
        <React.Fragment key={`row-${index}`}>{renderRow(rowStatuses)}</React.Fragment>
      ))}
    </div>
  );
};

export default TaskKanbanBoard;
